import type { GeneratedAd } from "../components/adsUtils";
import { isSyntheticLocalBananJob } from "../components/adsUtils";
import type {
  AdsWorkspaceState,
  PendingBananJob,
  PendingKlingJob,
  SubAgentChatState,
  SubAgentId,
} from "./useAdsWorkspaceStore.types";

// ── SessionStorage persistence for sub-agent state ──────────────────────────
// Survives page refresh within the same tab but not cross-tab or new sessions.

const SA_REVEALED_KEY = "wiser:revealedSubAgents";
const SA_SESSIONS_KEY = "wiser:subAgentSessions";
const SA_ACTIVE_KEY = "wiser:activeSubAgent";
const SA_PENDING_JOBS_KEY = "wiser:pendingJobs";
const SA_GENERATED_ADS_KEY = "wiser:generatedAds";
const SA_BRAND_ID_KEY = "wiser:selectedBrandId";
// Tags the flat keys above with the workspace they belong to. Without this,
// a fresh workspace would adopt whichever ads were left in sessionStorage by
// the previous session (e.g. across logout → new account login), which is
// how stale assets appear in the first workspace of a new account.
const SA_FLAT_WS_KEY = "wiser:flatStateWorkspace";

export function getFlatStateWorkspace(): string | null {
  try {
    const raw = sessionStorage.getItem(SA_FLAT_WS_KEY);
    return typeof raw === "string" && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

export function setFlatStateWorkspace(workspaceId: string | null) {
  try {
    if (workspaceId) sessionStorage.setItem(SA_FLAT_WS_KEY, workspaceId);
    else sessionStorage.removeItem(SA_FLAT_WS_KEY);
  } catch {
    // sessionStorage full or unavailable — silently skip
  }
}

// Clears the flat sessionStorage keys (but not the workspace tag). Called when
// entering a workspace with no adoptable state so leftovers from a previous
// session/account don't bleed into the fresh workspace.
export function clearFlatPersistedState() {
  try {
    sessionStorage.removeItem(SA_REVEALED_KEY);
    sessionStorage.removeItem(SA_SESSIONS_KEY);
    sessionStorage.removeItem(SA_ACTIVE_KEY);
    sessionStorage.removeItem(SA_PENDING_JOBS_KEY);
    sessionStorage.removeItem(SA_GENERATED_ADS_KEY);
    sessionStorage.removeItem(SA_BRAND_ID_KEY);
  } catch {
    /* ignore */
  }
}

// Module-load hydration: only return persisted state when the flat keys are
// tagged with a workspace (i.e. they were written during this session, not
// inherited from a logged-out account). `setScopeWorkspace` will adopt them
// only if the tag matches the workspace being entered, so an "unowned" flat
// state is effectively quarantined until the next workspace writes its own.
function flatStateIsAdoptable(): boolean {
  return getFlatStateWorkspace() !== null;
}

export function hydrateSelectedBrandId(): string | null {
  if (!flatStateIsAdoptable()) return null;
  try {
    const raw = sessionStorage.getItem(SA_BRAND_ID_KEY);
    return typeof raw === "string" && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

export function persistSelectedBrandId(brandId: string | null) {
  try {
    if (brandId) {
      sessionStorage.setItem(SA_BRAND_ID_KEY, brandId);
    } else {
      sessionStorage.removeItem(SA_BRAND_ID_KEY);
    }
  } catch {
    // sessionStorage full or unavailable — silently skip
  }
}

export function hydrateRevealedSubAgents(): SubAgentId[] {
  let revealed: SubAgentId[] = [];
  if (flatStateIsAdoptable()) {
    try {
      const raw = sessionStorage.getItem(SA_REVEALED_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) revealed = parsed;
      }
    } catch {
      // fall through and use defaults
    }
  }
  return revealed;
}

export function hydrateActiveSubAgent(): SubAgentId | null {
  if (!flatStateIsAdoptable()) return null;
  try {
    const raw = sessionStorage.getItem(SA_ACTIVE_KEY);
    if (
      raw === "banan" ||
      raw === "vid" ||
      raw === "scout" ||
      raw === "gads" ||
      raw === "meta_ads" ||
      raw === "higgsfield" ||
      raw === "higgsfield_cli" ||
      raw === "agent_v"
    ) {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}

export function hydrateSubAgentSessionId(agentId: SubAgentId): string | null {
  if (!flatStateIsAdoptable()) return null;
  try {
    const raw = sessionStorage.getItem(SA_SESSIONS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.[agentId] === "string" ? parsed[agentId] : null;
  } catch {
    return null;
  }
}

const STALE_JOB_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

/** Synthetic local-Banan start-image jobs (jobId `local-banan-*`) have no
 *  managed-chat status record and are owned entirely by the dispatch's
 *  add→finally-remove lifecycle. They must never be persisted: a reload or
 *  workspace switch mid-generation would rehydrate one as a zombie that the
 *  sub-agent panel polls forever (404 every 5s) until the 30-min TTL clears
 *  it. Strip them on both the read and write paths. */
function dropSyntheticBanan(jobs: PendingBananJob[]): PendingBananJob[] {
  return Array.isArray(jobs) ? jobs.filter((j) => !isSyntheticLocalBananJob(j.jobId)) : [];
}

export function hydratePendingJobs(): {
  pendingBananJobs: PendingBananJob[];
  pendingKlingJobs: PendingKlingJob[];
} {
  if (!flatStateIsAdoptable()) return { pendingBananJobs: [], pendingKlingJobs: [] };
  try {
    const raw = sessionStorage.getItem(SA_PENDING_JOBS_KEY);
    if (!raw) return { pendingBananJobs: [], pendingKlingJobs: [] };
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const filterStale = <T extends { dispatchedAt: number }>(jobs: T[]): T[] =>
      Array.isArray(jobs) ? jobs.filter((j) => now - j.dispatchedAt < STALE_JOB_THRESHOLD_MS) : [];
    return {
      pendingBananJobs: dropSyntheticBanan(filterStale(parsed.pendingBananJobs ?? [])),
      pendingKlingJobs: filterStale(parsed.pendingKlingJobs ?? []),
    };
  } catch {
    return { pendingBananJobs: [], pendingKlingJobs: [] };
  }
}

const MAX_PERSISTED_ADS = 50;

export function hydrateGeneratedAds(): GeneratedAd[] {
  if (!flatStateIsAdoptable()) return [];
  try {
    const raw = sessionStorage.getItem(SA_GENERATED_ADS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_PERSISTED_ADS) : [];
  } catch {
    return [];
  }
}

export function persistSubAgentState(
  revealedSubAgents: SubAgentId[],
  subAgentChats: Record<SubAgentId, SubAgentChatState>,
  activeSubAgent: SubAgentId | null,
) {
  try {
    sessionStorage.setItem(SA_REVEALED_KEY, JSON.stringify(revealedSubAgents));
    const sessions: Record<string, string | null> = {};
    for (const id of [
      "banan",
      "vid",
      "scout",
      "gads",
      "meta_ads",
      "higgsfield",
      "higgsfield_cli",
      "agent_v",
    ] as SubAgentId[]) {
      sessions[id] = subAgentChats[id]?.sessionId ?? null;
    }
    sessionStorage.setItem(SA_SESSIONS_KEY, JSON.stringify(sessions));
    if (activeSubAgent) {
      sessionStorage.setItem(SA_ACTIVE_KEY, activeSubAgent);
    } else {
      sessionStorage.removeItem(SA_ACTIVE_KEY);
    }
  } catch {
    // sessionStorage full or unavailable — silently skip
  }
}

export function persistPendingJobs(
  pendingBananJobs: PendingBananJob[],
  pendingKlingJobs: PendingKlingJob[],
) {
  try {
    sessionStorage.setItem(
      SA_PENDING_JOBS_KEY,
      JSON.stringify({ pendingBananJobs: dropSyntheticBanan(pendingBananJobs), pendingKlingJobs }),
    );
  } catch {
    // sessionStorage full or unavailable
  }
}

export function persistGeneratedAds(generatedAds: GeneratedAd[]) {
  try {
    sessionStorage.setItem(
      SA_GENERATED_ADS_KEY,
      JSON.stringify(generatedAds.slice(0, MAX_PERSISTED_ADS)),
    );
  } catch {
    // sessionStorage full or unavailable
  }
}

// ── Per-workspace state save/restore (survives workspace switch) ────────────

const SA_WS_STATE_PREFIX = "wiser:ws:";

interface PersistedWorkspaceState {
  revealedSubAgents: SubAgentId[];
  subAgentSessions: Record<string, string | null>;
  activeSubAgent: SubAgentId | null;
  pendingBananJobs: PendingBananJob[];
  pendingKlingJobs: PendingKlingJob[];
  generatedAds: GeneratedAd[];
  selectedBrandId?: string | null;
}

export function saveWorkspaceCompanionState(workspaceId: string, state: AdsWorkspaceState) {
  try {
    const data: PersistedWorkspaceState = {
      revealedSubAgents: state.revealedSubAgents,
      subAgentSessions: {
        banan: state.subAgentChats.banan.sessionId,
        vid: state.subAgentChats.vid.sessionId,
        scout: state.subAgentChats.scout.sessionId,
        gads: state.subAgentChats.gads.sessionId,
        meta_ads: state.subAgentChats.meta_ads.sessionId,
        higgsfield: state.subAgentChats.higgsfield.sessionId,
        higgsfield_cli: state.subAgentChats.higgsfield_cli.sessionId,
        agent_v: state.subAgentChats.agent_v.sessionId,
        moodboard: state.subAgentChats.moodboard.sessionId,
      },
      activeSubAgent: state.activeSubAgent,
      pendingBananJobs: dropSyntheticBanan(state.pendingBananJobs),
      pendingKlingJobs: state.pendingKlingJobs,
      generatedAds: state.generatedAds.slice(0, MAX_PERSISTED_ADS),
      selectedBrandId: state.selectedBrandId,
    };
    sessionStorage.setItem(`${SA_WS_STATE_PREFIX}${workspaceId}`, JSON.stringify(data));
  } catch {
    // quota exceeded or unavailable
  }
}

export function restoreWorkspaceCompanionState(workspaceId: string): {
  revealedSubAgents: SubAgentId[];
  activeSubAgent: SubAgentId | null;
  pendingBananJobs: PendingBananJob[];
  pendingKlingJobs: PendingKlingJob[];
  generatedAds: GeneratedAd[];
  subAgentChats: Record<SubAgentId, SubAgentChatState>;
  selectedBrandId: string | null;
} | null {
  try {
    const raw = sessionStorage.getItem(`${SA_WS_STATE_PREFIX}${workspaceId}`);
    if (!raw) return null;
    const data: PersistedWorkspaceState = JSON.parse(raw);
    const now = Date.now();
    const filterStale = <T extends { dispatchedAt: number }>(jobs: T[]): T[] =>
      Array.isArray(jobs) ? jobs.filter((j) => now - j.dispatchedAt < STALE_JOB_THRESHOLD_MS) : [];
    return {
      revealedSubAgents: Array.isArray(data.revealedSubAgents) ? data.revealedSubAgents : [],
      activeSubAgent: data.activeSubAgent ?? null,
      pendingBananJobs: dropSyntheticBanan(filterStale(data.pendingBananJobs ?? [])),
      pendingKlingJobs: filterStale(data.pendingKlingJobs ?? []),
      generatedAds: Array.isArray(data.generatedAds)
        ? data.generatedAds.slice(0, MAX_PERSISTED_ADS)
        : [],
      subAgentChats: {
        banan: {
          sessionId: data.subAgentSessions?.banan ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        vid: {
          sessionId: data.subAgentSessions?.vid ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        scout: { sessionId: null, sessionCreatedAt: null, cachedMessages: [] },
        gads: {
          sessionId: data.subAgentSessions?.gads ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        // Migrate the legacy `ads_cli` key (Mads v1/v2 sessions lived there
        // before the slot rename) into the new `meta_ads` slot if the legacy
        // key still holds a live session id.
        meta_ads: {
          sessionId:
            data.subAgentSessions?.meta_ads ??
            (data.subAgentSessions as { ads_cli?: string | null })?.ads_cli ??
            null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        higgsfield: {
          sessionId: data.subAgentSessions?.higgsfield ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        higgsfield_cli: {
          sessionId: data.subAgentSessions?.higgsfield_cli ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        agent_v: {
          sessionId: data.subAgentSessions?.agent_v ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
        moodboard: {
          sessionId: data.subAgentSessions?.moodboard ?? null,
          sessionCreatedAt: null,
          cachedMessages: [],
        },
      },
      selectedBrandId:
        typeof data.selectedBrandId === "string" && data.selectedBrandId.length > 0
          ? data.selectedBrandId
          : null,
    };
  } catch {
    return null;
  }
}
